/**
 * `users.user_type` is an enum(client|contact) on the backend.
 *
 * It is an IDENTITY label, not an authorization axis: the backend grants a
 * client and a contact exactly the same capabilities, and scopes every read by
 * user_id. Use it for copy and for the sign-in account chooser — never to hide
 * functionality. See @/features/auth/capabilities.
 */
export type UserType = 'client' | 'contact';

/** The user object returned by POST /auth/login and GET /auth/me. */
export interface PortalUser {
  uuid: string;
  name: string;
  email: string;
  user_type: UserType;
  phone: string | null;
  /**
   * True while the account is still on the generated password that was
   * emailed when it was provisioned. The API seals every /my/* endpoint
   * with 403 until it clears, so this is a mirror of a server-side rule
   * rather than a cosmetic hint.
   */
  must_change_password: boolean;
}

export interface LoginInput {
  email: string;
  password: string;
  /** Only sent when disambiguating after an `account_type_required` response. */
  user_type?: UserType;
}

/** `data` of a successful POST /auth/verify-otp — the only source of a token. */
export interface LoginResponse {
  token: string;
  token_type: string;
  /** Seconds until the token expires, or null if tokens never expire. */
  expires_in: number | null;
  user: PortalUser;
}

/**
 * `data` of POST /auth/login when the password was right — which is always,
 * since email OTP is mandatory for every portal account.
 *
 * Note the status is 200, not a 4xx: the first factor succeeded, so this is a
 * step in a sign-in rather than a failure. That is why it is discriminated on
 * `reason` in the success path and not caught as an error.
 */
export interface OtpRequiredResponse {
  reason: 'otp_required';
  /**
   * Opaque handle on the half-finished sign-in, good for one use and about five
   * minutes. It stands in for the server session rock-software uses — this API
   * is stateless, so the client carries the state instead. Treat it as a
   * credential: never log it, never persist it.
   */
  challenge: string;
  /** Seconds until the emailed code stops working. */
  expires_in: number;
}

/** POST /auth/login returns one shape or the other; never both. */
export function isOtpRequired(
  data: LoginResponse | OtpRequiredResponse,
): data is OtpRequiredResponse {
  return 'reason' in data && data.reason === 'otp_required';
}

export interface VerifyOtpInput {
  challenge: string;
  otp: string;
}

/** One option offered when an email matches more than one account. */
export interface AccountChoice {
  user_type: UserType;
  name: string;
}

/**
 * Result of a sign-in attempt.
 *
 * Neither non-terminal outcome is a failure the user can fix by retyping — one
 * is a question, the other a second step — so both are modelled as outcomes
 * rather than as errors. Only `signed-in` means a token was stored.
 */
export type SignInResult =
  | { outcome: 'signed-in' }
  | { outcome: 'account-type-required'; accounts: AccountChoice[] }
  | { outcome: 'otp-required'; challenge: string; expiresIn: number };

export interface ForgotPasswordInput {
  email: string;
  user_type?: UserType;
}

export interface ResetPasswordInput {
  token: string;
  email: string;
  user_type?: UserType;
  password: string;
  password_confirmation: string;
}

export interface ChangePasswordInput {
  password: string;
  password_confirmation: string;
}
